import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AudioWave } from "@/components/AudioWave";
import { Lock, Mail, ShieldCheck, Loader2, Sparkles, Radio, Fingerprint } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1683064325134-3acfdef9c6d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1NjQ4MDMyfDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Radio, t: "Realtime Transcript", d: "Speech-to-text Whisper Large V3 dengan timestamp presisi." },
  { icon: Fingerprint, t: "Voice Biometrics", d: "Kenali pembicara otomatis lewat voiceprint terenkripsi." },
  { icon: Sparkles, t: "AI Summary", d: "Notulen, keputusan, dan action items dibuat otomatis." },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-brand-900 p-14">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-900/85 to-cyanx/30" />
        <div className="relative z-10">
          <Logo size={52} dark tagline />
        </div>
        <div className="relative z-10 max-w-lg">
          <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight text-white">
            Notulen rapat<br />yang <span className="text-cyanx">berpikir</span> sendiri.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-300">
            Platform AI Meeting Intelligence untuk Pemerintah, BUMN, dan Enterprise. Sepenuhnya on-premise —
            tanpa ketergantungan cloud publik.
          </p>
          <div className="mt-10 opacity-70">
            <AudioWave bars={64} height={54} />
          </div>
          <div className="mt-10 space-y-5">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex gap-4">
                <div className="shrink-0 grid place-items-center h-9 w-9 rounded-xl bg-white/10 backdrop-blur text-cyanx">
                  <f.icon size={17} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.t}</p>
                  <p className="text-sm text-slate-400">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-400">© 2026 VECTRA · AES-256 · TLS 1.3 · On-Premise Ready</p>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 py-14 sm:px-12 bg-white">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-10">
            <Logo size={34} tagline />
          </div>
          <p className="label-xs">Masuk ke akun</p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-slate-900">Selamat datang kembali</h2>
          <p className="mt-2 text-sm text-slate-500">Gunakan kredensial organisasi Anda untuk melanjutkan.</p>

          <form onSubmit={submit} className="mt-9 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="nama@instansi.go.id" className="h-11 pl-10 rounded-xl" data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-brand-600 hover:text-brand-700" data-testid="forgot-password-link">
                  Lupa password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="h-11 pl-10 rounded-xl" data-testid="login-password-input"
                />
              </div>
            </div>

            {err && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="login-error">
                {err}
              </div>
            )}

            <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl grad-brand hover:opacity-95 text-white font-semibold" data-testid="login-submit-button">
              {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Memverifikasi…</> : "Masuk"}
            </Button>
          </form>

          <div className="mt-8 flex items-start gap-2.5 rounded-xl bg-slate-50 p-4">
            <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-600" />
            <p className="text-xs leading-relaxed text-slate-500">
              Sesi diamankan JWT dengan session timeout. Setiap aktivitas dicatat dalam audit trail.
            </p>
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">
            Butuh akses? Hubungi administrator organisasi Anda.
          </p>
        </div>
      </div>
    </div>
  );
}
